import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type Inbound, type InboundStatus } from "../api.js";
import { INBOUND_STATUSES } from "../constants.js";

function ReadonlyField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="field">
      <span>{label}</span>
      <div style={{ padding: "6px 0", color: value ? undefined : "var(--muted)" }}>{value ?? "—"}</div>
    </div>
  );
}

export function InboundDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [row, setRow] = useState<Inbound | null>(null);
  const [status, setStatus] = useState<InboundStatus>("open");
  const [resolution, setResolution] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const r = await api.getInbound(id);
        setRow(r);
        setStatus(r.status);
        setResolution(r.resolution ?? "");
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [id]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSaved(false);
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        status,
        resolution: resolution || null,
      };
      const updated = await api.updateInbound(id, payload);
      setRow(updated);
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (error && !row) return <section data-testid="inbound-detail"><div className="error-banner">{error}</div></section>;
  if (!row) return <section data-testid="inbound-detail"><div className="empty-state">読み込み中…</div></section>;

  const contextJson =
    row.context == null ? null : JSON.stringify(row.context, null, 2);

  return (
    <section data-testid="inbound-detail">
      <div className="toolbar" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>inbound 仕分け</h1>
        <button type="button" className="btn secondary" onClick={() => navigate("/inbound")}>← 一覧へ</button>
      </div>
      {error && <div className="error-banner" data-testid="form-error">{error}</div>}
      {saved && <div className="pill ok" data-testid="saved-pill">保存しました</div>}

      {/* intake payload — immutable history */}
      <div className="split">
        <ReadonlyField label="type" value={row.type} />
        <ReadonlyField label="service_id" value={row.service_id} />
      </div>
      <div className="split">
        <ReadonlyField label="contact" value={row.contact} />
        <ReadonlyField label="received_at" value={new Date(row.received_at).toLocaleString()} />
      </div>
      <div className="split">
        <ReadonlyField label="user_ref" value={row.user_ref} />
        <ReadonlyField label="app_version" value={row.app_version} />
      </div>
      <ReadonlyField label="location" value={row.location} />
      <div className="field">
        <span>本文</span>
        <div style={{ whiteSpace: "pre-wrap", padding: "6px 0" }}>{row.body ?? "—"}</div>
      </div>
      {contextJson && (
        <div className="field">
          <span>context</span>
          <pre style={{ background: "var(--code-bg, #f5f5f5)", padding: 12, overflowX: "auto", borderRadius: 6 }}>
            {contextJson}
          </pre>
        </div>
      )}

      {/* triage — the only mutable fields */}
      <form className="row" onSubmit={onSubmit} style={{ marginTop: 16, borderTop: "1px solid var(--border, #ddd)", paddingTop: 16 }}>
        <label className="field">
          <span>status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as InboundStatus)} data-testid="status-select">
            {INBOUND_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="field">
          <span>対応メモ (resolution)</span>
          <textarea
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            data-testid="resolution-input"
            placeholder="何が問題で何をしたか。重複なら「◯◯ と同じ」と書く。"
            style={{ minHeight: 80 }}
          />
        </label>
        {row.resolved_at && (
          <div style={{ color: "var(--muted)" }}>resolved_at: {new Date(row.resolved_at).toLocaleString()}</div>
        )}
        <div className="toolbar">
          <button type="submit" className="btn" disabled={submitting} data-testid="submit-btn">
            {submitting ? "保存中…" : "仕分けを保存"}
          </button>
        </div>
      </form>
    </section>
  );
}
